/* eslint-disable array-callback-return */
import { mergeStyleSets, TooltipHost, DirectionalHint, Icon } from "@fluentui/react";
import { Text } from "@fluentui/react";
import { complianceResult } from "../objects/complianceResult";
import { isWithinLast90Days } from "../utils";
import { ComplianceRule } from "../enums";

interface expFieldProps {
  complianceResults: Array<complianceResult>;
  formatedComplianceDate: string;
  lastActivityDate: Date;
  sensitivityLabel: string;
  setupToolFlag: string;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = mergeStyleSets({
  wrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    cursor: "default",
    padding: "2px 0",
  },
  dateText: {
    fontWeight: 600,
    fontSize: "13px",
    color: "#201f1e",
  },
  tooltipContent: {
    padding: "8px 4px",
    maxWidth: "280px",
  },
  tooltipTitle: {
    fontWeight: 600,
    fontSize: "13px",
    marginBottom: "6px",
    display: "block",
  },
  tooltipBody: {
    fontSize: "12px",
    color: "#605e5c",
    lineHeight: "18px",
    display: "block",
  },
  tooltipLink: {
    fontSize: "12px",
    color: "#0078d4",
    display: "block",
    marginTop: "8px",
    textDecoration: "none",
    selectors: {
      ":hover": {
        textDecoration: "underline",
      },
    },
  },
  // State chip styles
  chipBase: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
  },
  chipGreen: {
    background: "#dff6dd",
    color: "#107c10",
    border: "1px solid #bad80a",
  },
  chipRed: {
    background: "#fde7e9",
    color: "#a4262c",
    border: "1px solid #f1707b",
  },
  chipNeutral: {
    background: "#f3f2f1",
    color: "#605e5c",
    border: "1px solid #e1dfdd",
  },
});

// ── Tooltip content renderer ──────────────────────────────────────────────────
const TooltipContent: React.FC<{ message: string }> = ({ message }) => (
  <div className={styles.tooltipContent}>
    <Text className={styles.tooltipTitle}>Expiration Status</Text>
    <Text className={styles.tooltipBody}>{message}</Text>
    <a
      href="https://intel.sharepoint.com/sites/O365Help/SitePages/Workspace-Management-Tool.aspx#how-do-i-to-remediate-compliance-state"
      target="_blank"
      rel="noopener noreferrer"
      className={styles.tooltipLink}
      onClick={(e) => e.stopPropagation()}
    >
      Learn how to remediate →
    </a>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────
export function ExpirationDateField(props: expFieldProps) {
  let tooltipText = "";
  let formatedComplianceDate = props.formatedComplianceDate;
  let isTwoOwnerCompliant = false;

  // ── Determine two-owner compliance ────────────────────────────────────────
  props.complianceResults.forEach((compResult) => {
    if (compResult.complianceRule === ComplianceRule.TwoOwnerEnforcement) {
      isTwoOwnerCompliant = compResult.isCompliant;
    }
  });

  // ── Determine lease state ─────────────────────────────────────────────────
  type StateVariant = "compliant" | "expiring" | "expired" | "neutral";
  let variant: StateVariant = "neutral";

  props.complianceResults.forEach((compResult) => {
    if (compResult.complianceRule === ComplianceRule.LeaseExpiration) {
      const dateDifference = compResult.remainingLeaseDays;
      tooltipText = compResult.complianceMessage;

      if (props.setupToolFlag !== "") {
        variant = "compliant";
        tooltipText =
          "No action required while the workspace is kept active and compliant.";
        formatedComplianceDate = "Auto-Renewed";
      } else if (
        isWithinLast90Days(props.lastActivityDate) &&
        dateDifference > 0 &&
        isTwoOwnerCompliant &&
        props.sensitivityLabel !== "Internal - External"
      ) {
        variant = "compliant";
        tooltipText =
          "This workspace has been active for the last 3 months. No lease action required while the workspace is kept active and compliant.";
        formatedComplianceDate = "Auto-Renewed";
      } else if (dateDifference < 0) {
        variant = "expired";
      } else if (dateDifference <= 90) {
        variant = "expiring";
      } else {
        variant = "compliant";
      }
    }
  });

  // ── Chip appearance map ───────────────────────────────────────────────────
  const chipConfig: Record<
    StateVariant,
    { iconName: string; chipClass: string; label: string }
  > = {
    compliant: {
      iconName: "CheckMark",
      chipClass: `${styles.chipBase} ${styles.chipGreen}`,
      label: formatedComplianceDate,
    },
    expiring: {
      iconName: "Warning",
      chipClass: `${styles.chipBase} ${styles.chipRed}`,
      label: formatedComplianceDate,
    },
    expired: {
      iconName: "ErrorBadge",
      chipClass: `${styles.chipBase} ${styles.chipRed}`,
      label: formatedComplianceDate,
    },
    neutral: {
      iconName: "Clock",
      chipClass: `${styles.chipBase} ${styles.chipNeutral}`,
      label: formatedComplianceDate,
    },
  };

  const { iconName, chipClass, label } = chipConfig[variant];

  return (
    <div style={{ textAlign: "left" }}>
      <TooltipHost
        tooltipProps={{
          onRenderContent: () => <TooltipContent message={tooltipText} />,
        }}
        directionalHint={DirectionalHint.bottomLeftEdge}
        calloutProps={{
          gapSpace: 4,
          styles: {
            beak: { background: "#ffffff" },
            beakCurtain: { background: "#ffffff" },
            calloutMain: {
              background: "#ffffff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
              borderRadius: "6px",
              border: "1px solid #edebe9",
            },
          },
        }}
      >
        <div className={styles.wrapper}>
          <span className={chipClass}>
            <Icon
              iconName={iconName}
              styles={{ root: { fontSize: 11 } }}
            />
            {label}
          </span>
        </div>
      </TooltipHost>
    </div>
  );
}